import type { Permission } from '@thrivehrm/shared';
import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import { type CurrentUser, hasAnyPermission } from '@/lib/auth';

/**
 * Module registry (SRS EXT-10, DESIGN_SYSTEM §5). Each module contributes a sidebar group and
 * dashboard widgets; visibility is decided by permissions, never role names (EXT-2). Payroll
 * and Attendance register their own entries here when they exist (M6/M7).
 */
export interface NavItem {
  id: string;
  label: string;
  /** Route path, e.g. `/approvals`. */
  to: string;
  icon: LucideIcon;
  /** Shown when the user holds any of these (empty/omitted = every signed-in user). */
  requiredPermissions?: readonly Permission[];
  /** Shown only to people with reports (FR-MGR-3). */
  requiresReports?: boolean;
  /** Also shown to people with reports who lack `requiredPermissions` (e.g. managers deciding leave). */
  orHasReports?: boolean;
  /** Small count next to the label (e.g. requests waiting); renders nothing when zero. */
  badge?: ComponentType;
}

export interface DashboardWidget {
  id: string;
  title: string;
  component: ComponentType;
  requiredPermissions?: readonly Permission[];
  requiresReports?: boolean;
}

export interface AppModule {
  id: string;
  /** Sidebar group heading. */
  label: string;
  nav: readonly NavItem[];
  widgets?: readonly DashboardWidget[];
  /**
   * Where a subject-less approval request of a type opens (ADR-059), from its `subjectRef` —
   * e.g. `PAYROLL_RUN` → the run workspace. Keyed by request type.
   */
  approvalLinks?: Readonly<Record<string, (ref: Readonly<Record<string, string>>) => string>>;
}

interface Gated {
  requiredPermissions?: readonly Permission[];
  requiresReports?: boolean;
  orHasReports?: boolean;
}

export function isVisible(item: Gated, user: CurrentUser | null): boolean {
  if (!user) return false;
  if (item.orHasReports && user.hasReports) return true;
  if (item.requiresReports && !user.hasReports) return false;
  return hasAnyPermission(user, item.requiredPermissions);
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

/** Sidebar groups the user may see; empty groups are dropped. */
export function visibleNavGroups(
  modules: readonly AppModule[],
  user: CurrentUser | null,
): NavGroup[] {
  return modules
    .map((m) => ({
      id: m.id,
      label: m.label,
      items: m.nav.filter((item) => isVisible(item, user)),
    }))
    .filter((group) => group.items.length > 0);
}

/** Dashboard widgets the user may see, in module order. */
export function visibleWidgets(
  modules: readonly AppModule[],
  user: CurrentUser | null,
): DashboardWidget[] {
  return modules.flatMap((m) => (m.widgets ?? []).filter((w) => isVisible(w, user)));
}

/** Finds the nav item for a path (longest matching prefix), e.g. for page titles. */
export function findNavItem(modules: readonly AppModule[], path: string): NavItem | undefined {
  const items = modules.flatMap((m) => m.nav);
  return items
    .filter((i) => path === i.to || (i.to !== '/' && path.startsWith(`${i.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
}

/** The page a subject-less request opens, if its module registered one (ADR-059). */
export function approvalSubjectLink(
  modules: readonly AppModule[],
  request: { type: string; subjectRef: Readonly<Record<string, string>> | null },
): string | null {
  if (!request.subjectRef) return null;
  for (const m of modules) {
    const link = m.approvalLinks?.[request.type];
    if (link) return link(request.subjectRef);
  }
  return null;
}
